import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Icon,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    LinearProgress,
    CircularProgress,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import BaseDialog from '@/components/ui/BaseDialog';
import BaseAlert from '@/components/feedback/BaseAlert';
import { useTranslation } from 'react-i18next';
import BaseIconButton from '@/components/ui/BaseIconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BaseTextField from '@/components/form/BaseTextField';
import BaseButton from '@/components/ui/BaseButton';
import CloseIcon from '@mui/icons-material/Close';

type APDialogProps = {
    open: boolean;
    count: number;
    duplicateSubChannels?: number[];
    onClose: () => void;
    onQuickAction?: (key: string, payload?: any) => Promise<boolean | void> | boolean | void;
    quickActionLoadingKey?: string | null;
    alertState?: {
        message: string;
        severity: 'error' | 'warning' | 'info' | 'success';
        scope: 'quick' | 'advanced';
    } | null;
    onClearAlert?: () => void;
    initialSwitchChannelMode?: 'ENABLE' | 'DISABLE' | 'PROCESSING' | null;
    initialDestinationSubChannel?: number | null;
};

const fillSweep = keyframes({
    '0%': { transform: 'translateX(-120%)' },
    '50%': { transform: 'translateX(0%)' },
    '100%': { transform: 'translateX(120%)' },
});

const APDialog = React.memo(
    ({
        open,
        count,
        duplicateSubChannels = [],
        onClose,
        onQuickAction,
        quickActionLoadingKey,
        alertState,
        onClearAlert,
        initialSwitchChannelMode,
        initialDestinationSubChannel,
    }: APDialogProps) => {
        const { t } = useTranslation('common');
        const [channelValue, setChannelValue] = useState(81);
        const [channelInput, setChannelInput] = useState('81');
        const [isTimerRunning, setIsTimerRunning] = useState(false);
        const [isTimerProcessing, setIsTimerProcessing] = useState(false);
        const [isSwitchProcessing, setIsSwitchProcessing] = useState(false);
        const [isProcessingMode, setIsProcessingMode] = useState(false);
        const [isForceStopPending, setIsForceStopPending] = useState(false);
        const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
        const wasOpenRef = useRef(false);
        const [, setShowInfoAlert] = useState(true);
        const [showQuickInfoAlert, setShowQuickInfoAlert] = useState(true);
        const [showWarningAlert, setShowWarningAlert] = useState(true);
        const label = t('selectedAps', {
            count,
            defaultValue: 'Selected AP: {{count}}',
        });
        const [progressValue, setProgressValue] = useState(0);
        const showWarning = duplicateSubChannels.length > 0 && count > 1 && showWarningAlert;
        const warningText =
            duplicateSubChannels.length === 1
                ? t('Some selected APs share the same current sub channel: {{channels}}', {
                      channels: duplicateSubChannels[0],
                  })
                : t('Some selected APs share the same current sub channels: {{channels}}', {
                      channels: duplicateSubChannels.join(', '),
                  });
        const disableWakeUpOn = showWarning || count === 0;
        const disableWakeUpOff = count === 0;
        const disableUnlockAp = count !== 1;
        const disableLockAp = count === 0;
        const isSwitchTagDisabled = count !== 1 || duplicateSubChannels.length > 0;
        const isAdvancedDisabled = count > 1;
        const showProcessingUI = isProcessingMode && !isAdvancedDisabled;
        const isSwitchTagLoading = quickActionLoadingKey === 'switch-tag';
        const isChannelBusy = isSwitchProcessing || isTimerProcessing || isProcessingMode || isSwitchTagLoading;
        const parsedChannelInput = Number(channelInput);
        const isChannelInputValid =
            channelInput !== '' &&
            Number.isInteger(parsedChannelInput) &&
            parsedChannelInput >= 1 &&
            parsedChannelInput <= 97;
        const isSwitchButtonDisabled = isChannelBusy || (!isTimerRunning && !isChannelInputValid);

        const commitChannelInput = () => {
            if (channelInput === '') {
                setChannelInput(String(channelValue));
                return;
            }

            const num = Number(channelInput);
            if (!Number.isFinite(num)) {
                setChannelInput(String(channelValue));
                return;
            }

            const clamped = Math.min(97, Math.max(1, Math.trunc(num)));
            setChannelValue(clamped);
            setChannelInput(String(clamped));
        };

        useLayoutEffect(() => {
            if (!open) {
                wasOpenRef.current = false;
                return;
            }

            const isOpening = !wasOpenRef.current;
            wasOpenRef.current = true;

            const targetChannel = isAdvancedDisabled ? 81 : (initialDestinationSubChannel ?? 81);
            setChannelValue(targetChannel);
            setChannelInput(String(targetChannel));
            setIsTimerProcessing(false);
            setIsSwitchProcessing(false);
            setIsProcessingMode(initialSwitchChannelMode === 'PROCESSING');
            setIsForceStopPending(false);
            setShowInfoAlert(true);
            setShowQuickInfoAlert(true);
            setShowWarningAlert(true);

            if (isOpening) {
                setIsAdvancedExpanded(initialSwitchChannelMode === 'ENABLE' || initialSwitchChannelMode === 'PROCESSING');
            }

            if (count > 1) {
                setIsTimerRunning(false);
            } else {
                setIsTimerRunning(initialSwitchChannelMode === 'ENABLE');
            }
        }, [open, initialDestinationSubChannel, initialSwitchChannelMode, count, isAdvancedDisabled]);

        useEffect(() => {
            if (initialSwitchChannelMode !== 'PROCESSING') {
                setIsForceStopPending(false);
            }
        }, [initialSwitchChannelMode]);

        useEffect(() => {
            let interval: number | null = null;
            if (quickActionLoadingKey) {
                setProgressValue((prev) => (prev <= 5 ? 5 : prev));
                interval = window.setInterval(() => {
                    setProgressValue((prev) => {
                        const next = prev + Math.random() * 10;
                        return next >= 95 ? 95 : next;
                    });
                }, 250);
            } else {
                setProgressValue((prev) => (prev > 0 ? 100 : 0));
                const timeout = window.setTimeout(() => setProgressValue(0), 400);
                return () => window.clearTimeout(timeout);
            }
            return () => {
                if (interval) window.clearInterval(interval);
            };
        }, [quickActionLoadingKey]);

        const handleTimerToggle = async () => {
            if (isTimerProcessing || isSwitchProcessing || isProcessingMode) return;
            if (!isTimerRunning && !isChannelInputValid) return;
            setIsTimerProcessing(true);
            setIsSwitchProcessing(true);
            setIsProcessingMode(false);
            try {
                if (isTimerRunning) {
                    const ok = (await onQuickAction?.('timer-stop')) ?? false;
                    if (ok) {
                        setIsTimerRunning(false);
                        setIsProcessingMode(true);
                    }
                } else {
                    const nextChannel = Math.min(97, Math.max(1, Math.trunc(parsedChannelInput)));
                    setChannelValue(nextChannel);
                    setChannelInput(String(nextChannel));
                    const ok = (await onQuickAction?.('timer-start', { channel: nextChannel })) ?? false;
                    if (ok) {
                        setIsTimerRunning(false);
                        setIsProcessingMode(true);
                    }
                }
            } catch (err) {
                // noop
            } finally {
                setIsSwitchProcessing(false);
                setIsTimerProcessing(false);
            }
        };

        const handleForceStop = async () => {
            if (isTimerProcessing || isSwitchProcessing || isSwitchTagLoading || isForceStopPending) return;
            setIsTimerProcessing(true);
            setIsSwitchProcessing(true);
            try {
                const ok = (await onQuickAction?.('timer-force-stop')) ?? false;
                if (ok) {
                    setIsTimerRunning(false);
                    setIsProcessingMode(true);
                    setIsForceStopPending(true);
                }
            } catch (err) {
                // noop
            } finally {
                setIsSwitchProcessing(false);
                setIsTimerProcessing(false);
            }
        };

        const handleQuickActionClick = async (key: string) => {
            if (!onQuickAction) return;

            if (key === 'switch-tag' && count === 1) {
                const prevTimerRunning = isTimerRunning;
                const prevProcessingMode = isProcessingMode;
                const prevChannelValue = channelValue;
                const prevChannelInput = channelInput;

                setChannelValue(81);
                setChannelInput('81');
                setIsProcessingMode(false);
                setIsTimerRunning(false);

                const ok = (await onQuickAction(key)) ?? false;
                if (!ok) {
                    setChannelValue(prevChannelValue);
                    setChannelInput(prevChannelInput);
                    setIsTimerRunning(prevTimerRunning);
                    setIsProcessingMode(prevProcessingMode);
                } else {
                    setIsTimerRunning(false);
                    setIsProcessingMode(true);
                }
                return;
            }

            await onQuickAction(key);
        };

        const switchButtonLabel = showProcessingUI ? t('Processing') : isTimerRunning ? t('Stop') : t('Start');

        return (
            <BaseDialog
                dialogTitle={t('AP Management')}
                open={open}
                maxWidth="md"
                onClose={onClose}
                cancelButtonText=""
                showTopRightClose
                dialogContentProps={{ sx: { width: '100%' } }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            width: '100%',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                            {label}
                        </Typography>

                        {showWarning && (
                            <BaseAlert
                                severity="warning"
                                sx={{ px: 2, py: 1, flexShrink: 0 }}
                                message={warningText}
                                onClose={() => setShowWarningAlert(false)}
                            />
                        )}
                    </Box>

                    <Box sx={{ width: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            {t('Quick Actions')}
                        </Typography>

                        {count > 1 && showQuickInfoAlert && (
                            <Box sx={{ mb: 1 }}>
                                <BaseAlert
                                    severity="info"
                                    message={t('Some AP Management features work only with one AP selected')}
                                    sx={{ py: 1, mb: 1 }}
                                />
                            </Box>
                        )}

                        {quickActionLoadingKey && (
                            <Box sx={{ mb: 1 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mb: 0.25, display: 'block' }}
                                >
                                    {t('Processing action...')}
                                </Typography>
                                <LinearProgress
                                    variant="buffer"
                                    value={progressValue}
                                    valueBuffer={Math.min(100, progressValue + 12)}
                                    color="primary"
                                    sx={{
                                        height: 6,
                                        borderRadius: 999,
                                        backgroundColor: 'rgba(0,0,0,0.08)',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 999,
                                            transition: 'width 0.2s ease',
                                        },
                                        '& .MuiLinearProgress-bar2Buffer': {
                                            borderRadius: 999,
                                            transition: 'width 0.2s ease',
                                        },
                                    }}
                                />
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    sm: 'repeat(3, minmax(200px, 1fr))',
                                },
                                gap: 1,
                            }}
                        >
                            {[
                                {
                                    key: 'wakeup-on',
                                    label: t('Turn On Wake Up Mode'),
                                    iconClass: 'fa-toggle-on',
                                    disabled: disableWakeUpOn,
                                },
                                {
                                    key: 'wakeup-off',
                                    label: t('Turn Off Wake Up Mode'),
                                    iconClass: 'fa-toggle-off',
                                    disabled: disableWakeUpOff,
                                },
                                {
                                    key: 'unlock-ap',
                                    label: t('Unlock AP Network'),
                                    iconClass: 'fa-unlock',
                                    disabled: disableUnlockAp,
                                },
                                {
                                    key: 'lock-ap',
                                    label: t('Lock AP Network'),
                                    iconClass: 'fa-lock',
                                    disabled: disableLockAp,
                                },
                                {
                                    key: 'switch-tag',
                                    label: t('Switch TAG to AP Channel'),
                                    iconClass: 'fa-tower-broadcast',
                                    disabled: isSwitchTagDisabled,
                                },
                            ].map((action) => (
                                <BaseIconButton
                                    key={action.key}
                                    disabled={action.disabled || !!quickActionLoadingKey}
                                    onClick={() => {
                                        void handleQuickActionClick(action.key);
                                    }}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                        px: 1.25,
                                        py: 0.75,
                                        minWidth: 200,
                                        minHeight: 44,
                                        backgroundColor:
                                            quickActionLoadingKey === action.key ? 'action.hover' : 'background.paper',
                                        '&.Mui-disabled': {
                                            opacity: 0.55,
                                        },
                                        color: 'text.primary',
                                        overflow: 'visible',
                                    }}
                                    title={action.label}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.85,
                                            justifyContent: 'flex-start',
                                            width: '100%',
                                        }}
                                    >
                                        <Icon
                                            component="i"
                                            className={`far ${action.iconClass} fa-fw`}
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 20,
                                                lineHeight: 1,
                                                width: '1.25em',
                                                height: '1em',
                                                overflow: 'visible',
                                                flexShrink: 0,
                                                ml: 0.25,
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ fontSize: 14, lineHeight: 1.2 }}>
                                            {action.label}
                                        </Typography>
                                    </Box>
                                </BaseIconButton>
                            ))}
                        </Box>

                        {alertState?.scope === 'quick' && (
                            <Box sx={{ mt: 1 }}>
                                <BaseAlert
                                    severity={alertState.severity}
                                    message={alertState.message}
                                    onClose={onClearAlert}
                                    sx={{ px: 2, py: 1, whiteSpace: 'pre-line' }}
                                />
                            </Box>
                        )}
                    </Box>

                    <Accordion
                        expanded={isAdvancedExpanded}
                        onChange={(_event, expanded) => setIsAdvancedExpanded(expanded)}
                        sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                mt: 0,
                                minHeight: 50,
                                '&.Mui-expanded': { minHeight: 50 },
                                '& .MuiAccordionSummary-content': { margin: 0 },
                                '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
                            }}
                        >
                            <Typography variant="h6">{t('Advanced Management')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: count > 1 ? 0.5 : 1 }}
                        >
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {t('Switch Channel')}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    gap: 1.5,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {count > 1 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {t('Unavailable when multiple APs are selected.')}
                                    </Typography>
                                ) : (
                                    <>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: 320 }}>
                                            <Typography variant="body2">{t('Move tag in channel')}</Typography>
                                            <BaseTextField
                                                type="number"
                                                label=""
                                                size="small"
                                                value={channelInput}
                                                onChange={(e) => {
                                                    const next = e.target.value;
                                                    if (next === '') {
                                                        setChannelInput('');
                                                        return;
                                                    }
                                                    if (!/^\d+$/.test(next)) return;
                                                    setChannelInput(next);
                                                    const num = Number(next);
                                                    if (num >= 1 && num <= 97) {
                                                        setChannelValue(num);
                                                    }
                                                }}
                                                onBlur={commitChannelInput}
                                                inputProps={{ min: 1, max: 97 }}
                                                sx={{ width: 160, height: 40 }}
                                                disabled={isChannelBusy || isTimerRunning}
                                            />
                                        </Box>

                                        {showProcessingUI ? (
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    height: 40,
                                                    gap: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 200,
                                                        maxWidth: '100%',
                                                        height: 12,
                                                        borderRadius: 1.5,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        backgroundColor: 'grey.100',
                                                        boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
                                                        px: 1,
                                                        py: 0.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <LinearProgress
                                                        color="primary"
                                                        sx={{
                                                            width: '100%',
                                                            height: 6,
                                                            borderRadius: 999,
                                                            backgroundColor: 'rgba(0,0,0,0.08)',
                                                            '& .MuiLinearProgress-bar': { borderRadius: 999 },
                                                        }}
                                                    />
                                                </Box>
                                                <BaseIconButton
                                                    title={t('Force Stop')}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleForceStop();
                                                    }}
                                                    disabled={
                                                        isTimerProcessing ||
                                                        isSwitchProcessing ||
                                                        isSwitchTagLoading ||
                                                        isForceStopPending
                                                    }
                                                    sx={{
                                                        width: 20,
                                                        height: 20,
                                                        minWidth: 20,
                                                        alignItems: 'center',
                                                        borderRadius: '50%',
                                                        backgroundColor: 'error.main',
                                                        color: 'common.white',
                                                        border: '2px solid #fff',
                                                        boxShadow: '0 3px 8px rgba(0,0,0,0.15)',
                                                        '&:hover': { backgroundColor: 'error.dark' },
                                                        '&:disabled': {
                                                            backgroundColor: 'error.light',
                                                            color: 'common.white',
                                                            opacity: 0.8,
                                                        },
                                                    }}
                                                >
                                                    <CloseIcon
                                                        sx={{ fontSize: 12, width: 12, height: 12, lineHeight: 1 }}
                                                    />
                                                </BaseIconButton>
                                            </Box>
                                        ) : (
                                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                <BaseButton
                                                    onClick={handleTimerToggle}
                                                    disabled={isSwitchButtonDisabled}
                                                    disableRipple
                                                    disableElevation
                                                    sx={{
                                                        px: 3,
                                                        height: 40,
                                                        borderRadius: 1.5,
                                                        border: '1px solid',
                                                        borderColor:
                                                            isTimerProcessing || isSwitchProcessing
                                                                ? 'divider'
                                                                : isTimerRunning
                                                                  ? 'error.main'
                                                                  : 'success.main',
                                                        backgroundColor:
                                                            isTimerProcessing || isSwitchProcessing
                                                                ? 'grey.100'
                                                                : isTimerRunning
                                                                  ? 'error.main'
                                                                  : 'success.main',
                                                        color:
                                                            isTimerProcessing || isSwitchProcessing
                                                                ? 'text.primary'
                                                                : 'common.white',
                                                        boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
                                                        transition: 'transform 0.1s ease, background-color 0.2s ease',
                                                        flexShrink: 0,
                                                        position: 'relative',
                                                        overflow: 'visible',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 1,
                                                        '&::after': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            inset: 0,
                                                            background:
                                                                'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.37) 50%, transparent 100%)',
                                                            transform: 'translateX(-120%)',
                                                            animation:
                                                                isTimerRunning &&
                                                                !isTimerProcessing &&
                                                                !isSwitchProcessing
                                                                    ? `${fillSweep} 1.4s ease-in-out infinite`
                                                                    : 'none',
                                                            pointerEvents: 'none',
                                                        },
                                                        '&:hover': {
                                                            backgroundColor:
                                                                isTimerProcessing || isSwitchProcessing
                                                                    ? 'grey.200'
                                                                    : isTimerRunning
                                                                      ? 'error.dark'
                                                                      : 'success.dark',
                                                            borderColor:
                                                                isTimerProcessing || isSwitchProcessing
                                                                    ? 'divider'
                                                                    : isTimerRunning
                                                                      ? 'error.dark'
                                                                      : 'success.dark',
                                                            transform: 'translateY(-1px)',
                                                        },
                                                        '&:active': {
                                                            backgroundColor:
                                                                isTimerProcessing || isSwitchProcessing
                                                                    ? 'grey.200'
                                                                    : isTimerRunning
                                                                      ? 'error.dark'
                                                                      : 'success.dark',
                                                            borderColor:
                                                                isTimerProcessing || isSwitchProcessing
                                                                    ? 'divider'
                                                                    : isTimerRunning
                                                                      ? 'error.dark'
                                                                      : 'success.dark',
                                                            transform: 'translateY(0)',
                                                        },
                                                        '&.Mui-disabled': {
                                                            opacity: 0.65,
                                                            borderColor: 'divider',
                                                            backgroundColor: 'grey.100',
                                                            color: 'text.disabled',
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {switchButtonLabel}
                                                    </Typography>
                                                    {isTimerProcessing && (
                                                        <CircularProgress
                                                            size={18}
                                                            sx={{ color: 'primary.main', position: 'absolute' }}
                                                        />
                                                    )}
                                                </BaseButton>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>

                            {alertState?.scope === 'advanced' && (
                                <Box sx={{ mt: 1 }}>
                                    <BaseAlert
                                        severity={alertState.severity}
                                        message={alertState.message}
                                        onClose={onClearAlert}
                                        sx={{ px: 2, py: 1, whiteSpace: 'pre-line' }}
                                    />
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </BaseDialog>
        );
    },
);

export default APDialog;
